import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import {
  useClientesApi,
  ClienteItem,
  ClienteDto,
} from "../../../services/api/clientesApi";
import ClienteDataTable from "../../../components/module/admin/GenericDataTable/clientes/ClienteDataTable";
import ClienteModal from "../../../components/module/admin/ClienteModal/ClienteModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Clientes = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ClienteItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ClienteItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useClientesApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 40 },
    {
      field: "tipoDocumento",
      headerName: "Tipo Doc.",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.tipoDocumento?.Nombre || "",
    },
    { field: "NumeroDocumento", headerName: "Documento", flex: 1, minWidth: 120 },
    { field: "NombreCompleto", headerName: "Nombre Completo", flex: 1, minWidth: 200 },
    {
      field: "tipoCliente",
      headerName: "Tipo Cliente",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.tipoCliente?.Nombre || "",
    },
    { field: "Telefono", headerName: "Teléfono", flex: 1, minWidth: 120 },
    { field: "Correo", headerName: "Correo", flex: 1, minWidth: 180 },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton onClick={() => handleView(params.row.Id)} color="info" size="small">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await getAll();
      if (Array.isArray(data)) {
        setItems(data);
      }
    } catch (error) {
      console.error("Error loading items:", error);
      toast.error("No se pudieron cargar los clientes");
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ClienteItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleView = (id: number) => {
    navigate(`/administracion/clientes/view/${id}`);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción desactivará al cliente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El cliente ha sido eliminado correctamente.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el cliente.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este cliente?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: ClienteDto = {
            IdTipoCliente: item.IdTipoCliente,
            IdTipoDocumento: item.IdTipoDocumento,
            NumeroDocumento: item.NumeroDocumento,
            NombreCompleto: item.NombreCompleto,
            FechaNacimiento: item.FechaNacimiento,
            Telefono: item.Telefono,
            Direccion: item.Direccion,
            Correo: item.Correo,
            Imagen: item.Imagen,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del cliente ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: ClienteDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      const freshItems = await getAll();
      if (Array.isArray(freshItems)) {
        setItems(freshItems);
      }
      setIsModalOpen(false);
      toast.success("Cliente guardado correctamente");
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error al guardar el cliente";
      throw new Error(errorMessage);
    }
  };

  return (
    <PageLayout icon={PersonIcon} title="Clientes">
        <div style={{ padding: "24px" }}>
          <ClienteDataTable
            items={items}
            columns={columns}           
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <ClienteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Cliente" : "Nuevo Cliente"}
            initialData={
              editingItem
                ? {
                    IdTipoCliente: editingItem.IdTipoCliente,
                    IdTipoDocumento: editingItem.IdTipoDocumento,
                    NumeroDocumento: editingItem.NumeroDocumento,
                    NombreCompleto: editingItem.NombreCompleto,
                    FechaNacimiento: editingItem.FechaNacimiento,
                    Telefono: editingItem.Telefono,
                    Direccion: editingItem.Direccion,
                    Correo: editingItem.Correo,
                    Imagen: editingItem.Imagen,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Clientes;